import React, { useEffect, useState } from 'react';
import { fetchPortfolioHealth } from '../api';

const PortfolioHealthWidget = ({ userId, useCaseId = 'wealth_management' }) => {
    const [health, setHealth] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRebalanceModal, setShowRebalanceModal] = useState(false);
    const [showTradeModal, setShowTradeModal] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [confirmationType, setConfirmationType] = useState('');

    useEffect(() => {
        const getPortfolioHealth = async () => {
            try {
                setIsLoading(true);
                const data = await fetchPortfolioHealth(userId, useCaseId);
                setHealth(data);
            } catch (err) {
                console.error("Error fetching portfolio health:", err);
                setError("Failed to load portfolio health data");
            } finally {
                setIsLoading(false);
            }
        };

        getPortfolioHealth();
    }, [userId, useCaseId]);

    // Default fallback data
    const defaultHealth = {
        score: 85,
        rating: "Excellent",
        diversification: "A+",
        beta: 1.4,
        ytdReturn: 12.8
    };

    const displayHealth = health || defaultHealth;

    return (
        <div
            className="bg-gray-800/30 backdrop-blur-md rounded-lg relative overflow-hidden h-full"
            style={{
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.05), inset 0 0 0 1px rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.18)'
            }}
        >
            {/* Decorative elements for glass effect */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-500/10 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-500/10 rounded-full blur-2xl"></div>
            <div className="relative z-10 p-4 flex flex-col h-full">
                <h2 className="text-lg font-medium mb-4 text-white text-center">Portfolio Health</h2>

                {isLoading ? (
                    <div className="animate-pulse space-y-4 flex-1 flex flex-col justify-center">
                        <div className="h-24 bg-gray-700 rounded mx-auto w-24"></div>
                        <div className="flex justify-center space-x-2">
                            <div className="h-12 bg-gray-700 rounded w-20"></div>
                            <div className="h-12 bg-gray-700 rounded w-20"></div>
                            <div className="h-12 bg-gray-700 rounded w-20"></div>
                        </div>
                    </div>
                ) : error ? (
                    <div className="text-center text-red-500 py-4 flex-1 flex items-center justify-center">
                        Failed to load portfolio health data
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col justify-between">
                        <div className="flex items-center justify-center mt-4">
                            <div className="relative w-44 h-44">
                                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                                    <path
                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                        fill="none"
                                        strokeWidth="3"
                                        strokeDasharray="100, 100"
                                        className="stroke-gray-700"
                                    />
                                    <path
                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                        fill="none"
                                        stroke="#10B981"
                                        strokeWidth="3.5"
                                        strokeDasharray={`${displayHealth.score}, 100`}
                                        className="stroke-emerald-500"
                                        strokeLinecap="round"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <div className="text-5xl font-bold text-emerald-400">{displayHealth.score}</div>
                                    <div className="text-sm text-gray-400 mt-1">{displayHealth.rating}</div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 px-4 py-6">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-emerald-400 mb-1">{displayHealth.diversification}</div>
                                <div className="text-xs text-gray-400">Diversification</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-emerald-400 mb-1">{displayHealth.beta}</div>
                                <div className="text-xs text-gray-400">Beta</div>
                            </div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold mb-1 ${displayHealth.ytdReturn >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {displayHealth.ytdReturn >= 0 ? '+' : ''}{displayHealth.ytdReturn}%
                                </div>
                                <div className="text-xs text-gray-400">YTD Return</div>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3 px-4 pb-2">
                            <button 
                                onClick={() => setShowRebalanceModal(true)}
                                className="bg-emerald-600/80 hover:bg-emerald-600 text-white py-2.5 rounded-md text-sm font-medium transition-colors"
                            >
                                Rebalance
                            </button>
                            <button className="bg-emerald-600/80 hover:bg-emerald-600 text-white py-2.5 rounded-md text-sm font-medium transition-colors">
                                Report
                            </button>
                            <button 
                                onClick={() => setShowTradeModal(true)}
                                className="bg-emerald-600/80 hover:bg-emerald-600 text-white py-2.5 rounded-md text-sm font-medium transition-colors"
                            >
                                Trade
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showRebalanceModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowRebalanceModal(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-md mx-4 shadow-2xl border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                                Portfolio Rebalancing
                            </h3>
                        </div>
                        <p className="text-base text-gray-600 dark:text-gray-300 mb-6 leading-relaxed" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                            This service is not currently enabled for your account. Our team can help you set up automated portfolio rebalancing to maintain your target asset allocation.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowRebalanceModal(false)}
                                className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setConfirmationType('Rebalancing');
                                    setShowRebalanceModal(false);
                                    setShowConfirmation(true);
                                }}
                                className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-lg shadow-emerald-500/25" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                            >
                                Request Service
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showTradeModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowTradeModal(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-md mx-4 shadow-2xl border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                                Trading Service
                            </h3>
                        </div>
                        <p className="text-base text-gray-600 dark:text-gray-300 mb-6 leading-relaxed" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                            This service is not currently enabled for your account. Our team can help you set up trading capabilities to execute transactions directly through the platform.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowTradeModal(false)}
                                className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setConfirmationType('Trading');
                                    setShowTradeModal(false);
                                    setShowConfirmation(true);
                                }}
                                className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-lg shadow-emerald-500/25" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                            >
                                Request Service
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showConfirmation && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowConfirmation(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-md mx-4 shadow-2xl border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                                Request Submitted
                            </h3>
                        </div>
                        <p className="text-base text-gray-600 dark:text-gray-300 mb-6 leading-relaxed" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                            Thank you for your interest in our {confirmationType} service. A member of our team will contact you shortly to discuss enabling this feature for your account.
                        </p>
                        <div className="flex justify-end">
                            <button
                                onClick={() => setShowConfirmation(false)}
                                className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-lg shadow-emerald-500/25" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortfolioHealthWidget;